import InlineCode from '@/components/InlineCode';
import { txToast } from '@/components/TxToaster';
import { Button } from '@/components/ui/button';
import { useOpenPromptDialog } from '@/hooks/dialogs';
import { useCloseAllSheets } from '@/hooks/sheets';
import { useGlobalStatus } from '@/hooks/status';
import { useBackendApi } from '@/hooks/fetch';
import { cn } from '@/lib/utils';
import humanizeDuration from '@/lib/humanizeDuration';

const germanDuration = {
    de: {
        y: (count?: number) => count === 1 ? 'Jahr' : 'Jahren',
        mo: (count?: number) => count === 1 ? 'Monat' : 'Monaten',
        w: (count?: number) => count === 1 ? 'Woche' : 'Wochen',
        d: (count?: number) => count === 1 ? 'Tag' : 'Tagen',
        h: (count?: number) => count === 1 ? 'Stunde' : 'Stunden',
        m: (count?: number) => count === 1 ? 'Minute' : 'Minuten',
        s: (count?: number) => count === 1 ? 'Sekunde' : 'Sekunden',
        ms: (count?: number) => count === 1 ? 'Millisekunde' : 'Millisekunden',
        decimal: ',',
    },
};

const msToGermanDuration = humanizeDuration.humanizer({
    round: true,
    language: 'de',
    languages: germanDuration,
    units: ['h', 'm'],
});
import { PenLineIcon, PlayCircleIcon, PlusCircleIcon, XCircleIcon } from 'lucide-react';
import { useAdminPerms } from '@/hooks/auth';

//Prompt props
const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timezoneDiffMessage = (
    <p className='text-fc-red-soft'>
        Server-Zeitzone: <b>{window.txConsts.serverTimezone}</b> <br />
        Deine Zeitzone: <b>{browserTimezone}</b> <br />
        Nutze eine relative Zeit oder plane die absolute Uhrzeit in der Server-Zeitzone.
    </p>
)
const promptCommonProps = {
    suggestions: ['+5', '+10', '+15', '+30'],
    title: 'Wann soll der Server neu starten?',
    message: (<>
        <p>
            Mögliche Formate: <br />
            <ul className='list-disc ml-4'>
                <li>
                    <InlineCode>+MM</InlineCode> relative Zeit in Minuten
                    (Beispiel: <InlineCode>+15</InlineCode> für 15 Minuten ab jetzt.)
                </li>
                <li>
                    <InlineCode>HH:MM</InlineCode> absolute Uhrzeit im 24-Stunden-Format
                    (Beispiel: <InlineCode>23:30</InlineCode>.)
                </li>
            </ul>
        </p>
        {browserTimezone !== window.txConsts.serverTimezone && timezoneDiffMessage}
    </>),
    placeholder: '+15',
    required: true,
    isWide: true,
};

//Validate schedule time input for 24h format or relative time
const validateSchedule = (input: string) => {
    if (input.startsWith('+')) {
        const minutes = parseInt(input.substring(1));
        if (isNaN(minutes) || minutes < 1 || minutes >= 1440) {
            return false;
        }
    } else {
        const [hours, minutes] = input.split(':', 2).map(x => parseInt(x));
        if (
            typeof hours === 'undefined' || isNaN(hours) || hours < 0 || hours > 23
            || typeof minutes === 'undefined' || isNaN(minutes) || minutes < 0 || minutes > 59
        ) {
            return false;
        }
    }
    return true;
}


export default function ServerSchedule() {
    const closeAllSheets = useCloseAllSheets();
    const openPromptDialog = useOpenPromptDialog();
    const { hasPerm } = useAdminPerms();
    const schedulerApi = useBackendApi({
        method: 'POST',
        path: '/fxserver/schedule'
    });

    const globalStatus = useGlobalStatus();
    if (!globalStatus) {
        return <div>
            <h2>Nächster Neustart</h2>
            <span className='text-fc-txt3'>Wird geladen …</span>
        </div>
    }

    //Processing status
    const { scheduler } = globalStatus;
    let nextScheduledText = 'nichts geplant';
    let nextScheduledClasses = 'text-fc-txt3';
    let disableAddEditBtn = false;
    let showCancelBtn = false;
    let showEnableBtn = false;
    const hasScheduledRestart = typeof scheduler.nextRelativeMs === 'number';
    if (hasScheduledRestart) {
        const tempFlag = (scheduler.nextIsTemp) ? '(temporär)' : '';
        const relativeTime = msToGermanDuration(scheduler.nextRelativeMs);
        const isLessThanMinute = scheduler.nextRelativeMs < 60_000;
        if (isLessThanMinute) {
            disableAddEditBtn = true;
            nextScheduledText = `jetzt ${tempFlag}`;
        } else {
            nextScheduledText = `in ${relativeTime} ${tempFlag}`;
        }

        if (scheduler.nextSkip) {
            nextScheduledClasses = 'text-fc-txt3 line-through';
            if (!isLessThanMinute) {
                showEnableBtn = true;
            }
        } else {
            nextScheduledClasses = 'text-fc-yellow';
            if (!isLessThanMinute) {
                showCancelBtn = true;
            }
        }
    }


    //Handlers
    const onScheduleSubmit = (input: string) => {
        closeAllSheets();
        if (input.includes(',')) {
            txToast.error({
                title: 'Ungültige Neustartzeit.',
                msg: 'Mehrere Neustartzeiten werden ausschließlich in den Einstellungen verwaltet. Dieses Feld gilt nur für den nächsten temporären Neustart.',
            }, { duration: 10000 });
            return;
        }
        if (!validateSchedule(input)) {
            txToast.error(`Ungültige Neustartzeit: ${input}`)
            return;
        }
        schedulerApi({
            data: { action: 'setNextTempSchedule', parameter: input },
            toastLoadingMessage: 'Serverneustart wird geplant …',
        });
    }
    const handleEdit = () => {
        openPromptDialog({
            ...promptCommonProps,
            onSubmit: onScheduleSubmit,
            submitLabel: 'Ändern',
        });
    }
    const handleAddSchedule = () => {
        openPromptDialog({
            ...promptCommonProps,
            onSubmit: onScheduleSubmit,
            submitLabel: 'Planen',
        });
    }
    const handleCancel = () => {
        closeAllSheets();
        schedulerApi({
            data: { action: 'setNextSkip', parameter: true },
            toastLoadingMessage: 'Nächster Serverneustart wird ausgesetzt …',
        });
    }
    const handleEnable = () => {
        closeAllSheets();
        schedulerApi({
            data: { action: 'setNextSkip', parameter: false },
            toastLoadingMessage: 'Nächster Serverneustart wird aktiviert …',
        });
    }

    const hasSchedulePerms = hasPerm('control.server');

    return <div>
        <h2>Nächster Neustart</h2>
        <span className={cn(nextScheduledClasses)}>{nextScheduledText}</span>
        <div className='mt-2 flex flex-row flex-wrap justify-between gap-1.5'>
            {hasScheduledRestart ? (
                <Button
                    size='xs'
                    variant='secondary'
                    className='flex-grow'
                    disabled={!hasSchedulePerms || disableAddEditBtn}
                    onClick={handleEdit}
                >
                    <PenLineIcon className='h-3.5 w-3.5' /> Ändern
                </Button>
            ) : (
                <Button
                    size='xs'
                    variant='secondary'
                    className='flex-grow'
                    disabled={!hasSchedulePerms || disableAddEditBtn}
                    onClick={handleAddSchedule}
                >
                    <PlusCircleIcon className='h-3.5 w-3.5' /> Planen
                </Button>
            )}
            {showCancelBtn && (
                <Button
                    size='xs'
                    variant='secondary'
                    className='flex-grow'
                    onClick={handleCancel}
                    disabled={!hasSchedulePerms}
                >
                    <XCircleIcon className='h-3.5 w-3.5' /> Aussetzen
                </Button>
            )}
            {showEnableBtn && (
                <Button
                    size='xs'
                    variant='secondary'
                    className='flex-grow'
                    onClick={handleEnable}
                    disabled={!hasSchedulePerms}
                >
                    <PlayCircleIcon className='h-3.5 w-3.5' /> Aktivieren
                </Button>
            )}
        </div>
    </div>
}
